import { Dialog } from "@alifd/next";
import { BefRpcContext } from "@beflam/bef-core";
import { useContext, useRef } from "react";
import AddUser from "./AddUser";
import { COLUMNS, CONVERT_FIELDS, RPC_ASSEMBLY, RPC_MODULE } from "./constants";
import { ExcelImport } from "../ExcelImport";
import EditUserDialog from "./EditUserDialog";
import UpdateUserPassword from "./UpdateUserPassword";
import UserPermission from "./UserPermission";

export type DialogType = 'add' | 'edit' | 'updatePassword' | 'display' | 'excelImport' | 'userPermission';

export interface DialogVisibleState {
    add: boolean;
    edit: boolean;
    updatePassword: boolean;
    display: boolean;
    excelImport: boolean;
    userPermission: boolean;
}

interface UserDialogManagerProps {
    visible: DialogVisibleState;
    selectedUserId: string | null;
    selectedUserIds: any[];
    onAdd: (userRef: any) => Promise<void>;
    onEdit: (userRef: any) => Promise<void>;
    onUpdatePassword: (userRef: any) => Promise<void>;
    onAssignPermission: (userRef: any) => Promise<void>;
    onClose: (type: DialogType) => void;
    onExcelImportClose: () => void;
}

export function UserDialogManager({
    visible,
    selectedUserId,
    selectedUserIds,
    onAdd,
    onEdit,
    onUpdatePassword,
    onAssignPermission,
    onClose,
    onExcelImportClose,
}: UserDialogManagerProps) {
    const userRef = useRef<any>(null);
    const befRpc = useContext(BefRpcContext);

    const dataAdapter: any = {};
    dataAdapter.importExcel = async (data: any) => {
        return await befRpc.callRpcAsync<string>("", RPC_ASSEMBLY, RPC_MODULE, "Import", data);
    };

    return (
        <>
            <Dialog
                v2
                title="新增用户"
                width={800}
                visible={visible.add}
                footerActions={['cancel', 'ok']}
                okProps={{ className: 'custom-dialog-btn-ok' }}
                cancelProps={{ className: 'custom-dialog-btn-cancel' }}
                onClose={() => onClose('add')}
                onOk={() => onAdd(userRef)}
            >
                <AddUser ref={userRef} rpcAssembly={RPC_ASSEMBLY} rpcModule={RPC_MODULE} />
            </Dialog>

            <Dialog
                v2
                title="编辑用户"
                width={800}
                visible={visible.edit}
                footerActions={['cancel', 'ok']}
                okProps={{ className: 'custom-dialog-btn-ok' }}
                cancelProps={{ className: 'custom-dialog-btn-cancel' }}
                onClose={() => onClose('edit')}
                onOk={() => onEdit(userRef)}
            >
                <EditUserDialog ref={userRef} rpcAssembly={RPC_ASSEMBLY} rpcModule={RPC_MODULE} id={selectedUserId} />
            </Dialog>

            <Dialog
                v2
                title="重置用户密码"
                width={400}
                visible={visible.updatePassword}
                footerActions={['cancel', 'ok']}
                okProps={{ className: 'custom-dialog-btn-ok' }}
                cancelProps={{ className: 'custom-dialog-btn-cancel' }}
                onClose={() => onClose('updatePassword')}
                onOk={() => onUpdatePassword(userRef)}
            >
                <UpdateUserPassword ref={userRef} rpcAssembly={RPC_ASSEMBLY} rpcModule={RPC_MODULE} id={selectedUserId} />
            </Dialog>

            <Dialog
                v2
                title="用户详情"
                width={800}
                visible={visible.display}
                footer={false}
                onClose={() => onClose('display')}
            >
                <EditUserDialog ref={userRef} rpcAssembly={RPC_ASSEMBLY} rpcModule={RPC_MODULE} id={selectedUserId} readonly={true} />
            </Dialog>

            <Dialog
                v2
                title="Excel Import"
                visible={visible.excelImport}
                footer={false}
                closeMode={['close', 'esc']}
                width={650}
                onClose={onExcelImportClose}
            >
                <ExcelImport
                    importParams={{ dataAdapter, columns: COLUMNS, convertFields: CONVERT_FIELDS }}
                    onClose={onExcelImportClose}
                />
            </Dialog>

            <Dialog
                v2
                title="配置用户权限"
                width={800}
                visible={visible.userPermission}
                footerActions={['cancel', 'ok']}
                okProps={{ className: 'custom-dialog-btn-ok' }}
                cancelProps={{ className: 'custom-dialog-btn-cancel' }}
                onClose={() => onClose('userPermission')}
                onOk={() => onAssignPermission(userRef)}
            >
                <UserPermission ref={userRef} rpcAssembly={RPC_ASSEMBLY} rpcModule={RPC_MODULE} ids={selectedUserIds} />
            </Dialog>
        </>
    );
}
